import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Observable, map, filter } from 'rxjs';
import { DatePipe } from '@angular/common';
import { AccountsInterceptorService } from '@sbk/shared';
import { Router } from '@angular/router';
import { AccountsListGroupsConfigService } from '../journeys/accounts/accounts-list-groups-config.service';

@Injectable()
export class AccountsInterceptor implements HttpInterceptor {
  private readonly allowedRoutes = [
    '/my-accounts/list',
    '/my-accounts/manage'
  ];
  private hasInitialized = false;

  constructor(
    private datePipe: DatePipe,
    private accountsInterceptorService: AccountsInterceptorService,
    private router: Router,
    private accountsListGroupsConfig: AccountsListGroupsConfigService
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isGetRequest(request) && this.isGetArrangementsById(request)) {
      return next.handle(request).pipe(
        filter((event) => event instanceof HttpResponse),
        map((event: HttpResponse<any>) => {
          if (event.body?.additions?.limitLastUpdateDate) {
            const newBody = {
              ...event.body,
              additions: {
                ...event.body.additions,
                limitLastUpdateDate: this.datePipe.transform(
                  event.body.additions.limitLastUpdateDate,
                  'MMMM d, y',
                ),
              },
            };
            return event.clone({ body: newBody });
          }
          return event;
        }),
      );
    }

    // Reorder groups in arrangement-views API response
    if (this.isGetRequest(request) && this.isGetArrangementViews(request)) {
      return next.handle(request).pipe(
        filter((event) => event instanceof HttpResponse),
        map((event: HttpResponse<any>) => {
          const accountsListGroups = this.accountsListGroupsConfig.getAccountsListGroups();

          if (event.body?.groups && accountsListGroups) {
            const reorderedGroups = this.reorderGroups(event.body.groups, accountsListGroups);
            const newBody = { ...event.body, groups: reorderedGroups };
            return event.clone({ body: newBody });
          }
          return event;
        }),
      );
    }

    // Ensure execution only on allowed routes (handles query/matrix parameters)
    if (
      this.allowedRoutes.some(route => this.router.url.startsWith(route)) &&
      (this.isGetRequest(request) &&
        (this.isGetArrangements(request) || this.isGetTransactions(request) || this.isGetSingleArrangement(request)))
    ) {
      return next.handle(request).pipe(
        filter((event) => event instanceof HttpResponse),
        map((event: HttpResponse<any>) => {
          // On first load only, ensure default hidden state if no preference is saved
          if (!this.hasInitialized) {
            const hasStoredPreference = localStorage.getItem('sbk_account_balance_visibility') !== null;
            if (!hasStoredPreference) {
              this.accountsInterceptorService.setVisibility(false);
            }
            this.hasInitialized = true;
          }
          // MutationObserver will handle applying masks to new elements automatically
          return event;
        }),
      );
    }

    return next.handle(request);
  }

  isGetRequest(req: HttpRequest<any>) {
    return req.method.toUpperCase() === 'GET';
  }

  isGetArrangements(req: HttpRequest<any>) {
    return req.url.includes(`api/arrangement-manager/client-api/v2/productsummary`);
  }

  isGetSingleArrangement(req: HttpRequest<any>) {
    return req.url.includes(`api/arrangement-manager/client-api/v2/arrangements`);
  }

  isGetTransactions(req: HttpRequest<any>) {
    return req.url.includes(`api/transaction-manager/client-api/v2/transactions`);
  }

  isGetArrangementsById(req: HttpRequest<any>) {
    return req.url.includes(`api/arrangement-manager/client-api/v2/arrangements/`);
  }

  isGetArrangementViews(req: HttpRequest<any>) {
    const pattern = 'arrangement-manager/client-api/v2/arrangement-views';
    return req.url.includes(pattern) && !req.url.includes('/groups/');
  }

  /**
   * Reorders the groups object according to the accountsListGroups configuration.
   * Groups not in the configuration will appear after the configured groups.
   * Uses Object.defineProperty to ensure key order is preserved.
   * @param groups - The groups object from the API response
   * @param accountsListGroups - The configuration array defining the desired order
   * @returns A new object with groups reordered
   */
  private reorderGroups(groups: any, accountsListGroups: Array<{ groupId: string; label: string }>): any {
    if (!groups || !accountsListGroups || accountsListGroups.length === 0) {
      return groups;
    }

    // Create a new object without a prototype to ensure clean key ordering
    const reorderedGroups = Object.create(null);
    const configuredGroupIds = accountsListGroups.map(group => group.groupId);
    const existingGroupIds = Object.keys(groups);

    // Add groups in the order specified by the configuration
    configuredGroupIds.forEach(groupId => {
      if (groups[groupId]) {
        // Use defineProperty to ensure the property is added in this exact order
        Object.defineProperty(reorderedGroups, groupId, {
          value: groups[groupId],
          enumerable: true,
          writable: true,
          configurable: true
        });
      }
    });

    // Add any remaining groups that weren't in the configuration
    existingGroupIds.forEach(groupId => {
      if (!configuredGroupIds.includes(groupId)) {
        Object.defineProperty(reorderedGroups, groupId, {
          value: groups[groupId],
          enumerable: true,
          writable: true,
          configurable: true
        });
      }
    });

    return reorderedGroups;
  }
}
